import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFieldArrayReturn,
} from "react-hook-form";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import RouteForm from "@/components/trips/RouteForm";
import type { CreateTripInput } from "@/lib/validations";

const emptyRoute = {
  origin: "",
  destination: "",
  departureDate: "",
  timeConstraint: { type: "any" as const },
  position: 0,
  earliest_departure: "",
  latest_arrival: "",
  direct_flights_only: false,
  preferred_airlines: [] as string[],
  origin_assumed: false,
};

interface RoutesAccordionProps {
  register: UseFormRegister<CreateTripInput>;
  errors: FieldErrors<CreateTripInput>;
  watch: UseFormWatch<CreateTripInput>;
  setValue: UseFormSetValue<CreateTripInput>;
  fieldArray: UseFieldArrayReturn<CreateTripInput, "routes">;
  routeLabels: string[];
}

export default function RoutesAccordion({
  register,
  errors,
  watch,
  setValue,
  fieldArray,
  routeLabels,
}: RoutesAccordionProps) {
  const { fields, append, remove } = fieldArray;
  const count = fields.length;

  return (
    <AccordionItem value="routes">
      <AccordionTrigger>
        <span className="flex items-center gap-2">
          Routes
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
            {count}
          </span>
        </span>
      </AccordionTrigger>
      <AccordionContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="trip-name">Trip name</Label>
          <Input
            id="trip-name"
            placeholder="e.g. Trip to MIA"
            {...register("name")}
          />
          {errors.name && (
            <p className="text-destructive text-xs" role="alert">
              {errors.name.message as string}
            </p>
          )}
        </div>

        {errors.routes?.message && (
          <p className="text-sm text-destructive" role="alert">
            {errors.routes.message}
          </p>
        )}

        {fields.map((field, index) => (
          <RouteForm
            key={field.id}
            index={index}
            onRemove={() => remove(index)}
            canRemove={fields.length > 1}
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            legLabel={routeLabels[index]}
            onDestinationChange={(destination) => {
              // Auto-fill a sensible default trip name from the canonical
              // destination when the user hasn't named the trip yet.
              if (destination && !watch("name")?.trim()) {
                setValue("name", `Trip to ${destination}`, { shouldValidate: true });
              }
            }}
          />
        ))}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append({ ...emptyRoute, position: fields.length })}
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
          Add route
        </Button>
      </AccordionContent>
    </AccordionItem>
  );
}
